/* AthletePlatformsModal — inline editor for Hudl / MaxPreps / Opendorse
 * URLs launched from the public athlete profile page. Only rendered
 * when `isOwner` is true. Saves via the existing whitelisted
 * `PUT /api/me/athlete/profile` route — same endpoint used by the
 * `/athlete/me` dashboard so both surfaces stay in sync.
 *
 * iter533 · Native iOS framing + save reliability
 *   • Rendered via React portal to document.body so no parent stacking
 *     context can paint the BottomNav (z-70) or cookie banner (z-70)
 *     over the "Save platforms" button — same pattern that fixed the
 *     Founding-Athlete chat sheet.
 *   • Backdrop z-[79] + panel z-[80] (was flat z-[70] → collided with
 *     BottomNav on iPhone, letting the reviewer tap-through and miss
 *     the Save action entirely).
 *   • Body scroll lock while open (iOS Safari ignores `overflow:hidden`
 *     alone — we also freeze `position: fixed` + restore scrollY).
 *   • Belt-and-suspenders cache purge in `save()` — even if the global
 *     axios SWR write-through invalidation misses (e.g. a nested
 *     instance or a Capacitor plugin intercept), we manually drop the
 *     network-profile localStorage entry so navigating away and back
 *     never rehydrates from a stale snapshot missing the new URLs.
 *   • Composer padding respects env(safe-area-inset-bottom) so the
 *     home-indicator can't hide the Save button on iPhone X+.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Film, BarChart3, Handshake, ExternalLink, CheckCircle2, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SWR_KEY_PREFIX = 'flyt:swr:';

const ROWS = [
  { key: 'hudl_url',      label: 'Hudl',      caption: 'Game film',        icon: Film,      color: '#FF6600', placeholder: 'https://www.hudl.com/profile/…' },
  { key: 'maxpreps_url',  label: 'MaxPreps',  caption: 'Verified stats',   icon: BarChart3, color: '#E31C25', placeholder: 'https://www.maxpreps.com/…' },
  { key: 'opendorse_url', label: 'Opendorse', caption: 'NIL deals',        icon: Handshake, color: '#00A9E0', placeholder: 'https://opendorse.com/…' },
];

const normalizeUrl = (v) => {
  const s = (v || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
};

/**
 * iter533 · Direct cache purge. Belt-and-suspenders defense against
 * the write-through invalidation in swrCache.js silently missing on
 * native iOS. Drops every localStorage key that could shape the next
 * paint of the athlete's profile / share pages.
 */
const purgeAthleteCache = (slug) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const keysToDrop = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k || !k.startsWith(SWR_KEY_PREFIX)) continue;
      const raw = k.slice(SWR_KEY_PREFIX.length);
      // Any read that surfaces this athlete's fields (network profile,
      // public athlete GET, share page, roster tile aggregators).
      const touchesThisAthlete =
        raw.startsWith('/api/network/athletes')
        || raw.startsWith('/api/athletes')
        || raw.startsWith('/api/me')
        || raw.startsWith('/api/featured-reel')
        || raw.startsWith('/api/network/');
      if (touchesThisAthlete) keysToDrop.push(k);
      // Slug-scoped exact hits — belt on top of the family sweep.
      if (slug && raw.includes(`/${slug}`)) keysToDrop.push(k);
    }
    for (const k of keysToDrop) window.localStorage.removeItem(k);
  } catch {
    /* private mode / quota — cache is a bonus */
  }
};

const AthletePlatformsModal = ({ open, initial, onClose, onSaved, athleteSlug }) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset local state each time the modal opens with a fresh `initial`
  // snapshot so a re-open after a save picks up the persisted URLs.
  useEffect(() => {
    if (open) {
      setValues({
        hudl_url:      initial?.hudl_url || '',
        maxpreps_url:  initial?.maxpreps_url || '',
        opendorse_url: initial?.opendorse_url || '',
      });
    }
  }, [open, initial]);

  // iter533 · iOS-safe body scroll lock (Safari ignores `overflow:hidden`
  // on its own — we freeze the body at its current scroll position and
  // restore afterwards so the page doesn't jump to the top on close).
  useEffect(() => {
    if (!open) return undefined;
    const scrollY = window.scrollY;
    const { position, top, width, overflow } = document.body.style;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = position;
      document.body.style.top = top;
      document.body.style.width = width;
      document.body.style.overflow = overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const dirty = useMemo(() => {
    return ROWS.some((r) => (values[r.key] || '') !== (initial?.[r.key] || ''));
  }, [values, initial]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        updates: ROWS.reduce((acc, r) => {
          const raw = (values[r.key] || '').trim();
          acc[r.key] = raw ? normalizeUrl(raw) : null;
          return acc;
        }, {}),
      };
      const r = await axios.put(`${API}/me/athlete/profile`, payload, { withCredentials: true });
      const patch = {
        hudl_url:      r.data?.hudl_url || null,
        maxpreps_url:  r.data?.maxpreps_url || null,
        opendorse_url: r.data?.opendorse_url || null,
      };
      // iter533 · Belt-and-suspenders cache purge. Even if the axios
      // SWR write-through invalidation ran, this guarantees the next
      // mount of the network profile page reads fresh URLs from the
      // network instead of a stale localStorage snapshot.
      purgeAthleteCache(athleteSlug);
      onSaved?.(patch);
      toast.success('Platform links saved');
      onClose?.();
    } catch (e) {
      // 401 → the caller isn't the athlete owner. Surface a clear
      // message rather than the generic detail so they can re-auth.
      const status = e?.response?.status;
      const msg = status === 401 || status === 403
        ? 'You need to sign in as the athlete who owns this profile.'
        : (e?.response?.data?.detail || 'Save failed');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [values, athleteSlug, onSaved, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      {/* iter533 · Backdrop above BottomNav (z-70) + cookie banner
          (z-70) so nothing paints over the modal on native iOS. */}
      <div
        className="fixed inset-0 z-[79] bg-black/70 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
        data-testid="athlete-platforms-backdrop"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="platforms-modal-title"
        className="fixed inset-x-0 bottom-0 sm:inset-0 z-[80] flex items-end sm:items-center justify-center px-3 pb-3 sm:pb-6 pointer-events-none"
        data-testid="athlete-platforms-modal"
      >
        <div
          className="pointer-events-auto relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-[#0b0b10] ring-1 ring-white/10 shadow-2xl overflow-hidden flex flex-col"
          style={{
            maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 16px)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="p-5 sm:p-6 overflow-y-auto"
            style={{
              // Push content above the iOS home-indicator so nothing
              // is ever clipped and the Save button is always tappable.
              paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-cyan-300 font-bold">Athlete Platforms</p>
                <h2 id="platforms-modal-title" className="font-display text-2xl uppercase tracking-tight text-white leading-tight mt-1">
                  Add your links
                </h2>
                <p className="text-xs text-white/50 mt-1.5 leading-snug">Hudl film, MaxPreps stats, and Opendorse NIL. Saved to your profile instantly.</p>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 w-8 h-8 rounded-full bg-white/[0.06] ring-1 ring-white/10 text-white/70 hover:text-white hover:bg-white/[0.10] flex items-center justify-center transition"
                data-testid="platforms-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {ROWS.map(({ key, label, caption, icon: Icon, color, placeholder }) => {
                const current = values[key] || '';
                const saved = initial?.[key] || '';
                return (
                  <div key={key} className="rounded-xl bg-black/40 border border-white/10 p-3" data-testid={`platforms-modal-row-${key}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                        style={{ background: `${color}22`, color }}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white leading-tight">{label}</p>
                        <p className="text-[10px] uppercase tracking-widest text-white/40">{caption}</p>
                      </div>
                      {saved && (
                        <a
                          href={saved}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-cyan-300 hover:text-cyan-200"
                          data-testid={`platforms-modal-open-${key}`}
                        >
                          <ExternalLink className="w-3 h-3" /> Open
                        </a>
                      )}
                    </div>
                    <input
                      type="url"
                      value={current}
                      onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                      placeholder={placeholder}
                      className="w-full text-xs font-mono rounded-lg focus:outline-none"
                      style={{
                        // Beat the global App.css white-input rule so the
                        // typed URL is readable on the dark modal surface.
                        color: '#ffffff',
                        caretColor: '#ffffff',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        padding: '10px 12px',
                        fontSize: 16, // prevent iOS auto-zoom on focus
                      }}
                      data-testid={`platforms-modal-input-${key}`}
                    />
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={onClose}
                className="text-[11px] uppercase tracking-widest font-bold text-white/50 hover:text-white/80 px-3 py-2"
                data-testid="platforms-modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={!dirty || saving}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2.5 rounded-full bg-white text-black hover:bg-cyan-300 transition disabled:opacity-40 disabled:hover:bg-white"
                data-testid="platforms-modal-save"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {saving ? 'Saving…' : 'Save platforms'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default AthletePlatformsModal;
